/**
 * Structured Data Utilities
 * JSON-LD schema generators for SEO rich snippets
 * 
 * @module utils/structuredData
 * @see https://schema.org/
 * @see https://developers.google.com/search/docs/appearance/structured-data
 */

const BASE_URL = 'https://futurasmile.com';
const LOGO_URL = `${BASE_URL}/favicon-96x96.png`;

// ============================================================================
// Organization Schema
// ============================================================================

export interface OrganizationOptions {
    founder?: {
        name: string;
        jobTitle: string;
        description?: string;
    };
}

/**
 * Generates Organization schema
 * @see https://schema.org/Organization
 */
export const generateOrganizationSchema = (options?: OrganizationOptions) => {
    const schema: Record<string, any> = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${BASE_URL}#organization`,
        name: 'FuturaSmile',
        alternateName: 'FuturaSmile Inc.',
        url: BASE_URL,
        logo: {
            '@type': 'ImageObject',
            url: LOGO_URL,
            width: 96,
            height: 96,
        },
        description: 'AI-powered dental smile simulation platform for dental professionals',
        foundingDate: '2024',
        founder: options?.founder ? {
            '@type': 'Person',
            name: options.founder.name,
            jobTitle: options.founder.jobTitle,
            description: options.founder.description,
        } : undefined,
        address: {
            '@type': 'PostalAddress',
            addressCountry: 'US',
        },
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            availableLanguage: ['English'],
        },
        sameAs: [
            // Add social media URLs when available
            // 'https://twitter.com/futurasmile',
            // 'https://linkedin.com/company/futurasmile',
        ],
    };

    // Remove undefined values
    Object.keys(schema).forEach(key => {
        if (schema[key] === undefined) delete schema[key];
    });

    return schema;
};

// ============================================================================
// Website Schema
// ============================================================================

/**
 * Generates WebSite schema (for sitelinks search box)
 * @see https://schema.org/WebSite
 */
export const generateWebsiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}#website`,
    url: BASE_URL,
    name: 'FuturaSmile',
    description: 'AI-powered dental smile simulation platform',
    publisher: {
        '@id': `${BASE_URL}#organization`,
    },
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: `${BASE_URL}/blog?search={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
    },
});

// ============================================================================
// Product Schema
// ============================================================================

export interface ProductData {
    name: string;
    price: number;
    credits: number;
    description?: string;
}

/**
 * Generates Product schema for pricing items
 * @see https://schema.org/Product
 */
export const generateProductSchema = (product: ProductData) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${product.name} - ${product.credits} Credits`,
    description: product.description || `${product.credits} credits for AI smile simulations, videos, and portraits`,
    brand: {
        '@type': 'Brand',
        name: 'FuturaSmile',
    },
    offers: {
        '@type': 'Offer',
        url: `${BASE_URL}/pricing`,
        priceCurrency: 'USD',
        price: product.price.toFixed(2),
        priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        availability: 'https://schema.org/InStock',
        seller: {
            '@id': `${BASE_URL}#organization`,
        },
    },
    category: 'Dental Software',
});

// ============================================================================
// FAQ Schema
// ============================================================================

export interface FAQItem {
    question: string;
    answer: string;
}

/**
 * Generates FAQPage schema
 * @see https://schema.org/FAQPage
 */
export const generateFAQSchema = (faqs: FAQItem[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
        },
    })),
});

// ============================================================================
// HowTo Schema
// ============================================================================

export interface HowToStep {
    name: string;
    text: string;
    image?: string;
}

/**
 * Generates HowTo schema
 * @see https://schema.org/HowTo
 */
export const generateHowToSchema = (
    name: string,
    description: string,
    steps: HowToStep[],
    totalTime?: string // ISO 8601 duration format, e.g., "PT5M"
) => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    totalTime: totalTime || 'PT5M',
    step: steps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step.name,
        text: step.text,
        image: step.image,
    })),
});

// ============================================================================
// Breadcrumb Schema
// ============================================================================

export interface BreadcrumbItem {
    name: string;
    url: string;
}

/**
 * Generates BreadcrumbList schema
 * @see https://schema.org/BreadcrumbList
 */
export const generateBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
    })),
});

// ============================================================================
// BlogPosting Schema
// ============================================================================

export interface BlogPostData {
    title: string;
    description: string;
    url: string;
    image?: string;
    publishedTime: string;
    modifiedTime?: string;
    author: {
        name: string;
        url?: string;
    };
    section?: string;
    tags?: string[];
    wordCount?: number;
}

/**
 * Generates BlogPosting schema
 * @see https://schema.org/BlogPosting
 */
export const generateBlogPostingSchema = (post: BlogPostData) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    url: post.url.startsWith('http') ? post.url : `${BASE_URL}${post.url}`,
    image: post.image || `${BASE_URL}/og-image.jpg`,
    datePublished: post.publishedTime,
    dateModified: post.modifiedTime || post.publishedTime,
    author: {
        '@type': 'Person',
        name: post.author.name,
        url: post.author.url,
    },
    publisher: {
        '@id': `${BASE_URL}#organization`,
    },
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': post.url.startsWith('http') ? post.url : `${BASE_URL}${post.url}`,
    },
    articleSection: post.section,
    keywords: post.tags?.join(', '),
    wordCount: post.wordCount,
});

// ============================================================================
// Software Application Schema
// ============================================================================

/**
 * Generates SoftwareApplication schema
 * @see https://schema.org/SoftwareApplication
 */
export const generateSoftwareApplicationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'FuturaSmile',
    applicationCategory: 'HealthApplication',
    operatingSystem: 'Web Browser',
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free to start, pay-per-use credits',
    },
    aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '150',
        bestRating: '5',
        worstRating: '1',
    },
    featureList: [
        'AI-powered smile simulation',
        'Photorealistic treatment previews',
        'Video generation',
        'Patient reports',
        'HIPAA compliant',
    ],
});

// ============================================================================
// Combined Schemas for Homepage
// ============================================================================

/**
 * Generates all homepage schemas
 */
export const generateHomepageSchemas = (faqs?: FAQItem[]) => {
    const schemas = [
        generateOrganizationSchema({
            founder: {
                name: 'Dr. Ahmed Messaoudi',
                jobTitle: 'Founder & CEO',
                description: 'Dentist & AI Solutions Expert',
            },
        }),
        generateWebsiteSchema(),
        generateSoftwareApplicationSchema(),
    ];

    if (faqs && faqs.length > 0) {
        schemas.push(generateFAQSchema(faqs));
    }

    return schemas;
};
